import React, { useState, useEffect } from "react";
import "../Dashboard/Dashboard.scss";

const formatCurrency = (v) => {
  if (v === undefined || v === null || Number.isNaN(Number(v))) return "-";
  return v.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
};

const formatDecimal = (v, digits = 2) => {
  if (v === undefined || v === null || Number.isNaN(Number(v))) return "-";
  return Number(v).toFixed(digits);
};

const parseNumeric = (value) => {
  if (value === undefined || value === null) return NaN;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const cleaned = value
      .replace(/,/g, "")
      .replace(/%/g, "")
      .replace(/\$/g, "")
      .replace(/[^0-9.-]/g, "")
      .trim();
    if (!cleaned) return NaN;
    return Number(cleaned);
  }
  return Number(value);
};

const getArrayValue = (value, index = 0) => {
  if (Array.isArray(value)) return value[index];
  return value;
};

const parseExpirationFromDesc = (desc) => {
  if (typeof desc !== "string") return "-";
  const match = desc.match(/(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "-";
};

const getTickerFromDesc = (desc = "") => {
  const match = desc.match(/^[A-Z]+/);
  return match ? match[0] : "";
};

const parseStockOptionsPayload = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload
      .map((item) => {
        const secDesc = getArrayValue(item?.SecDesc, 0);
        const ticker = getTickerFromDesc(secDesc);
        if (!ticker) return null;

        const conQty = parseNumeric(getArrayValue(item?.ConQty, 0) || 0);
        const strike = parseNumeric(getArrayValue(item?.Strike, 0) || 0);
        const ask = parseNumeric(getArrayValue(item?.LastAskPrice, 0) || 0);
        const bid = parseNumeric(getArrayValue(item?.LastBidPrice, 0) || 0);
        const close = parseNumeric(getArrayValue(item?.ClosePrice, 0) || 0);
        const displayAsk = bid || close || ask || 0;
        const premiumPrice = ask || bid || 0;
        const premiumCost = premiumPrice * conQty * 100;
        const apiCost = parseNumeric(getArrayValue(item?.Cost, 0));
        const notional =
          parseNumeric(getArrayValue(item?.NotionalVal, 0) || 0) ||
          displayAsk * conQty * 100;

        return {
          ticker,
          secDesc: secDesc || "-",
          quantity: conQty,
          strike,
          expiration:
            getArrayValue(item?.ExpirationDate, 0) ||
            item?.ExpirationDate ||
            item?.EndOfProtection ||
            parseExpirationFromDesc(secDesc),
          displayAsk,
          ask,
          bid,
          underlyingClose: close,
          premiumPrice,
          premiumCost,
          apiCost,
          notional,
          status: "Pending Open",
        };
      })
      .filter(Boolean);
  }

  if (typeof payload === "object" && Array.isArray(payload.SecDesc)) {
    return payload.SecDesc.map((secDesc, index) => {
      const ticker = getTickerFromDesc(secDesc);
      const conQty = parseNumeric(getArrayValue(payload.ConQty, index) || 0);
      const strike = parseNumeric(getArrayValue(payload.Strike, index) || 0);
      const ask = parseNumeric(getArrayValue(payload.LastAskPrice, index) || 0);
      const bid = parseNumeric(getArrayValue(payload.LastBidPrice, index) || 0);
      const close = parseNumeric(getArrayValue(payload.ClosePrice, index) || 0);
      const displayAsk = bid || close || ask || 0;
      const premiumPrice = ask || bid || 0;
      const premiumCost = premiumPrice * conQty * 100;
      const apiCost = parseNumeric(getArrayValue(payload.Cost, index));
      const notional =
        parseNumeric(getArrayValue(payload.NotionalVal, index) || 0) ||
        displayAsk * conQty * 100;

      return {
        ticker,
        secDesc: secDesc || "-",
        quantity: conQty,
        strike,
        expiration:
          getArrayValue(payload.ExpirationDate, index) ||
          payload.ExpirationDate ||
          payload.EndOfProtection ||
          parseExpirationFromDesc(secDesc),
        displayAsk,
        ask,
        bid,
        underlyingClose: close,
        premiumPrice,
        premiumCost,
        apiCost,
        notional,
        status: "Pending Open",
      };
    }).filter((item) => item.ticker);
  }

  return [];
};

const collectStockOptionsFromNode = (node, collector) => {
  if (!node || typeof node !== "object") return;

  const directStockOptions = node.StockOptions || node.stockOptions;
  if (directStockOptions) {
    const parsed = parseStockOptionsPayload(directStockOptions);
    if (parsed.length) collector.push(...parsed);
  }

  Object.values(node).forEach((value) => {
    if (value && typeof value === "object") {
      collectStockOptionsFromNode(value, collector);
    }
  });
};

const extractConcentratedContracts = (response, protectionLevel) => {
  if (!response) return [];
  const result = response.result || response;
  const contracts = [];

  const chosen = result?.ChoosenOption;
  if (chosen && typeof chosen === "object") {
    const periods = Object.keys(chosen);
    if (periods.length > 0) {
      const periodNode = chosen[periods[0]];
      if (periodNode && typeof periodNode === "object") {
        const keys = Object.keys(periodNode);
        const target = Number(protectionLevel) / 100;

        let selectedKey = keys.find((key) => Number(key) === target);
        if (!selectedKey && Number.isFinite(target) && keys.length > 0) {
          selectedKey = keys.reduce((best, key) => {
            if (!best) return key;
            return Math.abs(Number(key) - target) <
              Math.abs(Number(best) - target)
              ? key
              : best;
          }, null);
        }

        if (selectedKey && periodNode[selectedKey]) {
          collectStockOptionsFromNode(periodNode[selectedKey], contracts);
        }
      }
    }
  }

  if (contracts.length === 0) {
    collectStockOptionsFromNode(result, contracts);
  }

  return contracts;
};

export default function ConcentratedHedgesTable({
  marketShieldResponse,
  portfolioValue = 1,
  protectionLevel,
  filteredTickers = [],
  stockOptions = null,
  recommendationMode = true,
  initialSelected = {},
  onSelectionChange = () => {},
}) {
  const res = marketShieldResponse || {};
  const result = res.result || res;
  const protPort = result.ProtPort || [];
  const contractsFromResponse = extractConcentratedContracts(
    res,
    protectionLevel,
  );
  const concentratedContracts =
    contractsFromResponse.length > 0
      ? contractsFromResponse
      : stockOptions
        ? parseStockOptionsPayload(stockOptions)
        : [];

  const concentratedUniverse =
    Array.isArray(filteredTickers) && filteredTickers.length > 0
      ? filteredTickers
      : result.OtherHolding;

  const concentratedTickers = Array.isArray(concentratedUniverse)
    ? new Set(
        concentratedUniverse.map((ticker) => String(ticker).toUpperCase()),
      )
    : null;

  const parentRows = (Array.isArray(protPort) ? protPort : [])
    .map((holding) => {
      const ticker = holding.Ticker || holding.ticker || "";
      const normalizedTicker = String(ticker).toUpperCase();

      if (
        concentratedTickers &&
        concentratedTickers.size > 0 &&
        !concentratedTickers.has(normalizedTicker)
      ) {
        return null;
      }

      const quantity = parseNumeric(holding.Quantity || holding.quantity || 0);
      const closePrice = parseNumeric(
        holding.ClosePrice || getArrayValue(holding.ClosePrice, 0) || 0,
      );
      const rawMarketValue = parseNumeric(
        holding.MktVal ||
          holding["Market Value"] ||
          holding.MarketValue ||
          holding.Market_Value ||
          0,
      );
      const marketValue =
        Number.isFinite(rawMarketValue) && rawMarketValue > 0
          ? rawMarketValue
          : quantity * closePrice;

      const contracts = concentratedContracts.filter(
        (contract) => contract.ticker === ticker,
      );

      return {
        ticker,
        quantity,
        marketValue,
        closePrice,
        contracts,
      };
    })
    .filter((row) => row && row.contracts.length > 0);

  const [selected, setSelected] = useState(() => initialSelected || {});

  useEffect(() => {
    const selectedContracts = [];
    let totalCost = 0;

    parentRows.forEach((row) => {
      row.contracts.forEach((contract, index) => {
        const key = `${row.ticker}-${index}`;
        if (selected[key]) {
          const askPremiumCost = Number(
            parseNumeric(contract.ask) * parseNumeric(contract.quantity) * 100,
          );
          const selectedCost = Number.isFinite(askPremiumCost)
            ? askPremiumCost
            : Number(contract.premiumCost || 0);
          totalCost += selectedCost;
          selectedContracts.push({
            ...contract,
            ticker: row.ticker,
            cost: selectedCost,
            displayCost: Number(contract.notional || 0),
          });
        }
      });
    });

    onSelectionChange({
      selectedMap: selected,
      selectedContracts,
      totalCost,
      availableTickers: parentRows.map((row) => row.ticker),
      tabType: "PutBuying",
      recommendationMode,
    });
  }, [selected, parentRows, recommendationMode, onSelectionChange]);

  return (
    <div className="dashboard-sec">
      <div className="dashboard-inner">
        <div className="dashboard-table">
          <div className="table-responsive">
            <table className="table table-striped callwriting">
              <thead>
                <tr>
                  <th className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={
                        parentRows.length > 0 &&
                        parentRows.every(
                          (row) =>
                            row.contracts.length > 0 &&
                            row.contracts.every(
                              (_, index) => selected[`${row.ticker}-${index}`],
                            ),
                        )
                      }
                      onChange={(event) => {
                        const next = {};
                        if (event.target.checked) {
                          parentRows.forEach((row) => {
                            row.contracts.forEach((_, index) => {
                              next[`${row.ticker}-${index}`] = true;
                            });
                          });
                        }
                        setSelected(next);
                      }}
                    />
                  </th>
                  <th>
                    SYMBOL{" "}
                    <img
                      src={`${process.env.PUBLIC_URL}/Arrow-icon.svg`}
                      alt="Arrow-icon"
                    />
                  </th>
                  <th>QUANTITY</th>
                  <th>ACTION</th>
                  <th>
                    PROTECTION LEVEL{" "}
                    <img
                      src={`${process.env.PUBLIC_URL}/Arrow-icon.svg`}
                      alt="Arrow-icon"
                    />
                  </th>
                  <th>
                    EXP DATE{" "}
                    <img
                      src={`${process.env.PUBLIC_URL}/Arrow-icon.svg`}
                      alt="Arrow-icon"
                    />
                  </th>
                  <th className="hoverable-selector">
                    <span className="selector">
                      ASK <img src="Assets/c_down.svg" alt="toggle" />
                    </span>
                  </th>
                  <th>STRIKE PRICE</th>
                  <th>COST $</th>
                  <th>% STOCK VALUE</th>
                  <th>% PORTFOLIO VALUE</th>
                </tr>
              </thead>
              <tbody>
                {parentRows.length === 0 && (
                  <tr>
                    <td colSpan={11} style={{ textAlign: "center" }}>
                      No concentrated holdings found in response
                    </td>
                  </tr>
                )}

                {parentRows.map((row) => (
                  <React.Fragment key={row.ticker}>
                    <tr className="ticker-header-row">
                      <td className="checkbox-column"></td>
                      <td>
                        <strong className="ticker-header-cell">
                          {row.ticker}
                        </strong>
                      </td>
                      <td>
                        <span className="ticker-header-cell">
                          {formatDecimal(row.quantity, 2)}
                        </span>
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>

                    {row.contracts.map((contract, index) => {
                      const key = `${row.ticker}-${index}`;
                      const cost = Number(contract.notional || 0);
                      const stockValueDenominator = Number(
                        parseNumeric(row.quantity) *
                          (Number.isFinite(
                            parseNumeric(contract.underlyingClose),
                          )
                            ? parseNumeric(contract.underlyingClose)
                            : parseNumeric(row.closePrice)),
                      );
                      const percentStockValue =
                        stockValueDenominator > 0
                          ? (cost / stockValueDenominator) * 100
                          : null;
                      const percentPortfolioValue =
                        Number(portfolioValue) > 0
                          ? (cost / Number(portfolioValue)) * 100
                          : null;

                      return (
                        <tr key={key} className="contract-row">
                          <td>
                            <input
                              type="checkbox"
                              checked={Boolean(selected[key])}
                              onChange={() => {
                                setSelected((prev) => ({
                                  ...prev,
                                  [key]: !prev[key],
                                }));
                              }}
                            />
                          </td>
                          <td>{contract.secDesc || "-"}</td>
                          <td>{formatDecimal(contract.quantity, 0)}</td>
                          <td
                            style={{
                              color:
                                contract.status === "Pending Open"
                                  ? "#FFA500"
                                  : contract.status === "Pending Roll"
                                    ? "#FFA500"
                                    : contract.status === "Pending Close"
                                      ? "red"
                                      : contract.status
                                        ? "#38BB55"
                                        : "#38BB55",
                            }}
                          >
                            {contract.status === "Pending Open"
                              ? "Pending Open"
                              : contract.status === "Pending Roll"
                                ? "Pending Roll"
                                : contract.status === "Pending Close"
                                  ? "Pending Close"
                                  : contract.status
                                    ? "Open"
                                    : "OPEN"}
                          </td>
                          <td>
                            {protectionLevel ? `${protectionLevel}%` : "-"}
                          </td>
                          <td>{contract.expiration || "-"}</td>
                          <td>
                            {contract.displayAsk
                              ? formatDecimal(contract.displayAsk, 2)
                              : "-"}
                          </td>
                          <td>
                            {contract.strike
                              ? formatCurrency(contract.strike)
                              : "-"}
                          </td>
                          <td>{cost ? formatDecimal(cost, 0) : "-"}</td>
                          <td>
                            {percentStockValue !== null
                              ? `${formatDecimal(percentStockValue, 3)}%`
                              : "-"}
                          </td>
                          <td>
                            {percentPortfolioValue !== null
                              ? `${formatDecimal(percentPortfolioValue, 3)}%`
                              : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
